import styled, { css } from 'styled-components';

import Attributes from './Attributes';
import BookNow from './BookNow';
import FavoriteVenue from './FavoriteVenue';
import Thumbnail from './Thumbnail';

import { TYPE_PAYMENT } from '../../../helper/type-payment';
import { formatCents } from '../../../helper/price';
import { getBookingDetails } from '../../helpers';
import { CalendarIcon, colors, CreditCardIcon, headingStyles, LocationIcon, mobile, notMobile, tablet } from '../../styles';

// The card's one interactive element. It carries the venue name, so the
// heading IS the click target -- the strongest shape for an answer engine, and
// the exact slot task 14 drops <a href="/venue/:slug"> into.
//
// ::after stretches it over the whole card, so a click anywhere still opens the
// venue. Anything else clickable inside the card must sit above that overlay.
//
// The @supports block: where :has() works, CardBackground draws the focus ring
// around the whole card, so this element drops its own. Where :has() does not
// work, it keeps the browser's own ring rather than showing none at all.
const CardTitleButton = styled.button`
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: inherit;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }

  @supports selector(:has(*)) {
    &:focus-visible {
      outline: none;
    }
  }
`;

// The card title is stretched over this element with ::after, so the card must
// be the positioned ancestor that inset: 0 resolves against.
//
// font-family and font-size are pinned to the values a <button> supplied. A
// <button> inherits neither and an <article> inherits both, so the tag change
// on its own would move every card from Arial to Inter.
//
// font-size reaches no text: every text block in the card sets its own. It
// reaches the thumbnail. The image element is display: inline, so it sits on a text
// baseline, and the room under that baseline scales with the root font size.
// Inheriting the page's 10px instead of the button's 13.3333px made every card
// 1px shorter. Measured on /search/ at 1440px.
//
// Arial here is an accident, not a design decision -- fixing it site-wide is
// its own Backlog item. See task 7b2.
//
// The :has() rule: an <article> cannot take focus, so the ring the card used to
// draw for itself is drawn for its title link instead, on the same box. The
// first outline declaration is the portable fallback for a browser that cannot
// parse the second; the second reproduces Chrome's own button ring exactly.
const CardBackground = styled.article`
  position: relative;
  display: block;
  width: 100%;
  box-sizing: border-box;
  border: solid 1px ${colors.lightBorder};
  background-color: transparent;
  font-family: Arial;
  font-size: 13.3333px;
  margin: 0 0 16px;
  padding: 0;
  border-radius: 6px;

  &:hover {
    border-color: ${colors.defaultBorder};
  }

  &:has(${CardTitleButton}:focus-visible) {
    outline: auto;
    outline: auto 1px -webkit-focus-ring-color;
  }

  ${({ selected }) =>
    selected &&
    css`
      @media (min-width: ${notMobile}) {
        background-color: ${colors.primaryBackground};
      }
    `}
`;

const CardLayout = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 16px;
  gap: 16px;
  color: ${colors.defaultText};
  text-align: left;

  @media (max-width: ${tablet}) {
    padding: 8px;
  }

  @media (max-width: ${mobile}) {
    display: block;
    padding: 16px;
  }
`;

const MobileCardContent = styled.div`
  display: none;

  @media (max-width: ${mobile}) {
    display: flex;
    flex-direction: column;
  }
`;

const CardContentWrapper = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: ${mobile}) {
    display: none;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
`;

const CardTitle = styled.h2`
  ${headingStyles}
  flex: 1 1 auto;
  font-size: 22px;
  line-height: 28px;
  margin: 2px 0;
`;

const CardLocation = styled.div`
  font-size: 12px;
  line-height: 14px;
  margin-top: 2px;

  @media (max-width: ${tablet}) {
    ${({ isBookingView }) =>
      isBookingView
        ? css`
            margin-top: 8px;
          `
        : css`
            margin-top: 4px;
          `}
  }

  @media (max-width: ${mobile}) {
    ${({ isBookingView }) =>
      isBookingView
        ? css`
            margin-top: 9px;
          `
        : css`
            margin-top: 6px;
          `}
  }

  svg {
    display: inline-block;
    height: 14px;
    width: 14px;
    margin-right: 2px;
    margin-top: -2px;
    fill: ${colors.primaryIcon};
  }
`;

const CardReservationData = styled.div`
  font-size: 12px;
  line-height: 16px;
  margin-top: 4px;

  @media (max-width: ${tablet}) {
    margin-top: 7px;
  }

  @media (max-width: ${mobile}) {
    margin-top: 9px;
  }

  svg {
    display: inline-block;
    height: 14px;
    width: 14px;
    margin-right: 2px;
    margin-top: -3px;
    fill: ${colors.primaryIcon};
  }
`;

const CardReservationPending = styled.div`
  font-size: 12px;
  line-height: 16px;
  margin-top: 4px;
  background-color: ${colors.primaryButton};
  width: max-content;
  padding: 4px 8px;
  border-radius: 10px;

  @media (max-width: ${tablet}) {
    margin-top: 7px;
  }

  @media (max-width: ${mobile}) {
    margin-top: 9px;
  }
  span {
    color: ${colors.whiteBackground};
  }
`;
const DefaultContent = ({ venue, location, isSelected, onCardClick }) => {
  return (
    <>
      <CardHeader>
        <CardTitle>
          <CardTitleButton onClick={onCardClick}>{venue.name}</CardTitleButton>
        </CardTitle>
        <FavoriteVenue venue={venue} venueIsSelected={isSelected} />
      </CardHeader>

      <CardLocation>
        <LocationIcon />
        {location}
      </CardLocation>

      <Attributes venue={venue} />
    </>
  );
};

const BookingContent = ({ venue, location, dateLabel, bookingCostLabel, onCardClick }) => {
  // Billing B3.0a: the rest is due (record status `reserved`).
  const isReserved = venue?.booking?.isReserved === true;
  const typeBooking = venue?.booking?.typeBooking;
  // Billing B3.1: the stored balance, with the fee (old: half the venue total, no fee).
  const pending = venue?.booking?.pendingCents;

  return (
    <>
      <CardHeader>
        <CardTitle>
          <CardTitleButton onClick={onCardClick}>{venue.name}</CardTitleButton>
        </CardTitle>
      </CardHeader>

      <CardLocation isBookingView>
        <LocationIcon />
        {location}
      </CardLocation>

      <CardReservationData>
        <CalendarIcon />
        {dateLabel}
      </CardReservationData>
      <CardReservationData>
        <CreditCardIcon />
        {bookingCostLabel} - Taxes and fees not included
      </CardReservationData>
      {isReserved && typeBooking === TYPE_PAYMENT.PARTIAL_PAYMENT && pending != null && (
        <CardReservationPending>
          <span>Pending Payment: {formatCents(pending)}</span>
        </CardReservationPending>
      )}

      <Attributes venue={venue} />
    </>
  );
};

const Venue = ({ venue, selected, setSelectedVenue, onClick }) => {
  const { isBooking: isBookingView, location, ...booking } = getBookingDetails(venue);

  const isSelected = !!selected && selected.id === venue.id;

  const isDefaultView = !isBookingView;

  const onCardClick = () => {
    setSelectedVenue(venue.id);
    if (onClick) onClick();
  };

  const content = isBookingView ? (
    <BookingContent venue={venue} location={location} onCardClick={onCardClick} {...booking} />
  ) : (
    <DefaultContent venue={venue} isSelected={isSelected} location={location} onCardClick={onCardClick} />
  );

  return (
    <CardBackground selected={isSelected}>
      <CardLayout>
        <MobileCardContent>{content}</MobileCardContent>

        <Thumbnail venue={venue} />

        <CardContentWrapper>
          {content}

          {isDefaultView && <BookNow venue={venue} />}
        </CardContentWrapper>

        {isDefaultView && (
          <MobileCardContent>
            <BookNow venue={venue} />
          </MobileCardContent>
        )}
      </CardLayout>
    </CardBackground>
  );
};

export default Venue;
